// ─── HADIAH PEMBELIAN di keranjang pembeli — helper MURNI ───────────────────
//
// Rancangan: docs/RENCANA-hadiah-pembelian.md §6.3 (+ §14.7 Tahap 2B). Dari isi
// keranjang (baris BIASA saja — H6: bundel tak dihitung syarat) + data aturan
// toko, susun kartu "Hadiah untukmu": hak, dorongan "kurang N pcs" / "kurang
// Rp X", pilihan varian hadiah, dan muatan `p_gifts`. Server tetap penentu hak
// saat checkout (mig 8680; kuota mig 8740 — hak dijepit sisa kuota, H15;
// syarat lintas produk & nilai belanja mig 8780 — setiap baris punya SATU
// pemilik, H24; nilai = harga keranjang sebelum voucher, H25).

import {
  defaultGiftClaim,
  fillGiftClaim,
  giftCheckoutPayload,
  giftEntitlementMeasure,
  giftLineOwner,
  giftLineValue,
  giftMissingAmount,
  giftMissingPcs,
  giftOwnerCandidates,
  giftQuotaCap,
  giftRewardChoices,
  giftRuleAllowsLevel,
  giftRuleCoversLine,
  giftRuleCoversProduct,
  giftRuleSentence,
  giftTargetLabel,
  isGiftRuleRunning,
  lockedGiftReward,
  normalizeGiftClaim,
  type GiftClaimLine,
  type GiftLineRef,
  type GiftMeasure,
  type GiftProductRef,
  type GiftQuota,
  type GiftRewardChoice,
  type GiftRewardVariant,
  type GiftRule
} from "@/lib/gift";
import type { GiftLineKey } from "@/lib/giftNesting";
import { mapOrderGiftRule, type OrderGiftRule } from "@/lib/giftOrders";
import { isOrderableStatus } from "@/lib/products";

/** Produk syarat / hadiah / isi keranjang sebagaimana dibaca toko (anon, ber-filter toko). */
export type GiftProductInfo = {
  id: string;
  slug: string;
  name: string;
  productType: "preorder" | "ready_stock";
  status: string;
  imageUrl: string | null;
  /** Merek & kategori (teks, dicocokkan BY NAMA) — cakupan aturan bertarget (mig 8780). */
  brand: string | null;
  category: string | null;
  /** id opsi → id grup opsi (urut grup & opsi produk). */
  optionGroupOf: Map<string, string>;
  /** id opsi → nama opsi (urut grup & opsi produk). */
  optionNameOf: Map<string, string>;
  /** Varian AKTIF produk HADIAH (RLS toko); kosong untuk produk yang hanya syarat. */
  variants: GiftRewardVariant[];
};

export type StorefrontGiftData = {
  /**
   * SEMUA aturan aktif & dalam periode toko, urut nama — termasuk yang produk
   * hadiahnya tak terbaca: pemilik baris (H24) dinilai atas semuanya, seperti
   * server. Level dinilai pemanggil.
   */
  rules: GiftRule[];
  /** Produk syarat produk-tunggal, produk hadiah, dan produk isi keranjang. */
  products: Map<string, GiftProductInfo>;
  /** id varian baris keranjang produk syarat produk-tunggal → `option_ids`. Absen = tak dihitung. */
  variantOptionIds: Map<string, string[]>;
  /** Sisa kuota aturan BERKUOTA (`gift_rule_quota`, mig 8740). Absen = tak terbatas. */
  quotas: Map<string, GiftQuota>;
};

export const EMPTY_GIFT_DATA: StorefrontGiftData = {
  rules: [],
  products: new Map(),
  variantOptionIds: new Map(),
  quotas: new Map()
};

/** Baris keranjang minimal (cermin `CartState` / `CartLine`). */
export type GiftCartProduct = {
  productId: string;
  /** `unitPrice` = harga keranjang (sebelum voucher); kosong = harga menyusul (tak dihitung nilai). */
  lines: ReadonlyArray<{ variationId: string; qty: number; unitPrice?: number | null }>;
};

/**
 * Barang TAK TERIKAT pesanan sebelumnya milik pembeli yang login, per aturan
 * (`my_gift_carry`, mig 8880, H55): pcs, pcs berharga, dan nilainya (harga
 * sebelum voucher). Sesudah checkout server menggabungnya dengan pesanan baru
 * (gabungan lintas pesanan, H54) — keranjang ikut menghitungnya.
 */
export type GiftCarry = { pcs: number; pricedPcs: number; value: number };

/** Baris `my_gift_carry` → sisa per aturan; baris rusak / kosong = null. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftCarryRow(row: any): { ruleId: string; carry: GiftCarry } | null {
  const ruleId = String(row?.rule_id ?? "");
  const num = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  };
  const carry = {
    pcs: Math.floor(num(row?.carry_pcs)),
    pricedPcs: Math.floor(num(row?.carry_priced_pcs)),
    value: Math.round(num(row?.carry_value))
  };
  return ruleId && carry.pcs > 0 ? { ruleId, carry } : null;
}

/**
 * Pembelian LAMA pelanggan yang masih bisa bertambah — kandidat langkah (a')
 * mesin gabungan (`gift_auto_attach_order`, mig 8880): hadiah > 0 dan ≤ hak,
 * urut tertua. `carry` = sisa barang tak terikat pesanan lain MILIK aturannya
 * yang dicakup SNAPSHOT pembelian ini — dihitung server
 * (`my_gift_open_purchases`, Hadiah Tahap 6-2), jadi cakupan barang lama tak
 * pernah ditebak klien. Barang keranjang / editor dinilai cermin snapshot
 * (`giftRuleCoversLine` atas `rule`).
 */
export type GiftOpenPurchase = {
  id: string;
  ruleId: string;
  createdAt: string;
  rule: OrderGiftRule;
  /** Pesanan yang memuat barang syaratnya (Edit Pesanan: pesanan yang disunting dimodelkan langkah a). */
  orderIds: string[];
  syaratPcs: number;
  syaratValue: number;
  hak: number;
  hadiahPcs: number;
  carry: GiftCarry;
};

/** Baris `my_gift_open_purchases` → pembelian lama; baris rusak = null. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftOpenPurchaseRow(row: any): GiftOpenPurchase | null {
  const id = String(row?.order_gift_id ?? "");
  const ruleId = String(row?.rule_id ?? "");
  if (!id || !ruleId) return null;
  const whole = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  };
  return {
    id,
    ruleId,
    createdAt: String(row?.created_at ?? ""),
    rule: mapOrderGiftRule(row?.rule),
    orderIds: Array.isArray(row?.order_ids) ? row.order_ids.map((o: unknown) => String(o)).filter(Boolean) : [],
    syaratPcs: whole(row?.syarat_pcs),
    syaratValue: whole(row?.syarat_nilai),
    hak: whole(row?.hak),
    hadiahPcs: whole(row?.hadiah_pcs),
    carry: {
      pcs: whole(row?.carry_pcs),
      pricedPcs: whole(row?.carry_priced_pcs),
      value: whole(row?.carry_value)
    }
  };
}

/** Barang baru (keranjang / editor) milik satu aturan, sebatas bahan cakupan snapshot. */
type GiftOwnedLine = {
  product: GiftProductRef;
  line: GiftLineRef;
  optionGroupOf: ReadonlyMap<string, string>;
  qty: number;
  value: number;
};

/**
 * Langkah (a') mesin gabungan (mig 8880) atas satu aturan: pembelian lama
 * TERTUA yang haknya naik menyerap SEMUA barang yang dicakup snapshot-nya —
 * sisa pesanan lain (`carry` server) + barang baru `lines` (kosong bila barang
 * baru sudah jadi pembelian keranjang lebih dulu). null = tak ada yang naik.
 */
function giftOldAbsorb(
  open: readonly GiftOpenPurchase[],
  lines: readonly GiftOwnedLine[]
): { id: string; pcs: number; cartPcs: number; cartPricedPcs: number; cartValue: number; carry: GiftCarry } | null {
  for (const p of open) {
    const nilai = (p.rule.triggerMeasure ?? "pcs") === "nilai";
    let cartPcs = 0;
    let cartPricedPcs = 0;
    let cartValue = 0;
    for (const l of lines) {
      if (nilai && l.value <= 0) continue;
      if (!giftRuleCoversLine(p.rule, l.product, l.line, l.optionGroupOf)) continue;
      cartPcs += l.qty;
      if (l.value > 0) {
        cartPricedPcs += l.qty;
        cartValue += l.value;
      }
    }
    const joinPcs = (nilai ? p.carry.pricedPcs + cartPricedPcs : p.carry.pcs + cartPcs);
    if (joinPcs <= 0) continue;
    const hakAfter = giftEntitlementMeasure(p.rule, p.syaratPcs + joinPcs, p.syaratValue + p.carry.value + cartValue);
    if (hakAfter <= p.hak) continue;
    return { id: p.id, pcs: hakAfter - p.hak, cartPcs, cartPricedPcs, cartValue, carry: p.carry };
  }
  return null;
}

export type GiftCartStatus =
  /** Hak ada & hadiah dipilih — ikut checkout. */
  | "siap"
  /**
   * Hak baru tercapai BERSAMA sisa pesanan sebelumnya — tak diklaim checkout;
   * server memberi hadiahnya (varian bawaan) sesudah pesanan dibuat (H55).
   */
  | "otomatis"
  /** Syarat belum tercapai — hanya dorongan "tambah N pcs". */
  | "kurang"
  /** Hak ada, tapi tak ada varian yang bisa diberikan (stok habis). */
  | "habis"
  /** Produk hadiah sedang tak bisa dipesan (PO ditutup / tak berstatus ready). */
  | "tidak_bisa_dipesan"
  /** Kuota hadiah (total, atau jatah pembeli ini) sudah habis — mig 8740. */
  | "kuota_habis";

export type GiftCartEntry = {
  rule: GiftRule;
  sentence: string;
  reward: GiftProductInfo;
  /** Ukuran syarat aturan ini. */
  measure: GiftMeasure;
  /** Pcs syarat MILIK aturan ini (ukuran nilai: baris berharga saja). */
  triggerPcs: number;
  /** Nilai syarat (rupiah, harga keranjang sebelum voucher) baris milik aturan ini. */
  triggerValue: number;
  /** Ukuran nilai: pcs milik aturan ini yang harganya belum ada (harga menyusul) — tak dihitung. */
  unpricedPcs: number;
  /**
   * Baris keranjang yang jadi syarat aturan ini (ukuran nilai: yang berharga
   * saja — cermin stempel server). Bahan penempatan kartu di bawah baris
   * syaratnya (`giftEntryHosts`, putusan pemilik 2026-10-28).
   */
  syaratKeys: GiftLineKey[];
  /**
   * Hak pcs hadiah dari KERANJANG SAJA, sudah dijepit sisa kuota (H15) — yang
   * diklaim checkout (`p_gifts`); server menilai klaim atas isi keranjang.
   */
  entitlement: number;
  /**
   * Pcs / nilai syarat dari pesanan sebelumnya yang ikut dihitung (H55; ukuran
   * nilai: pcs berharga). 0 = tak ada, atau server tak memakainya (aturan
   * sekali yang sudah tercapai oleh keranjang — H56).
   */
  carryPcs: number;
  carryValue: number;
  /**
   * Hadiah yang tercapai karena sisa pesanan sebelumnya, di atas `entitlement`
   * — dipilihkan server (varian bawaan) sesudah pesanan dibuat, tanpa pemilih.
   */
  autoEntitlement: number;
  /**
   * Hadiah yang bertambah di pembelian LAMA aturan ini (langkah a' mesin
   * gabungan, Hadiah Tahap 6-2): barang ini — atau sisa pesanan sebelumnya —
   * melengkapi pembelian kelipatan yang masih bisa bertambah. Diberikan mesin
   * sesudah Keep Produk (varian hadiah pembelian itu lebih dulu), tanpa pemilih.
   */
  oldPcs: number;
  /** Sisa kuota untuk pembeli ini (total ∧ jatahnya); null = tak terbatas. */
  remaining: number | null;
  /** Hak dari syarat melebihi sisa kuota → yang diberikan hanya sisanya. */
  quotaCapped: boolean;
  /** Pcs syarat yang masih kurang menuju hak berikutnya (ukuran pcs); 0 = tak ada / ukuran nilai. */
  missing: number;
  /** Rupiah syarat yang masih kurang menuju hak berikutnya (ukuran nilai); 0 = tak ada / ukuran pcs. */
  missingAmount: number;
  /** Hak (klaim + otomatis) bila kekurangannya ditambahkan (bahan kalimat dorongan). */
  nextEntitlement: number;
  choices: GiftRewardChoice[];
  locked: GiftRewardChoice | null;
  claim: GiftClaimLine[];
  status: GiftCartStatus;
};

// ─── Pilihan pembeli tersimpan (D3 rencana §13.4) ───────────────────────────

/**
 * Pilihan varian hadiah — ikut objek keranjang tersimpan
 * (`galea-preorder-cart`, kunci `gifts`) supaya tak hilang saat halaman dimuat
 * ulang. Basi itu wajar: `cartGiftEntries` merapikannya terhadap hak & stok
 * terkini (`normalizeGiftClaim`). Hadiah tak bisa dilewati lagi (H40, mig
 * 8860) — kunci `skipped` keranjang lama diabaikan.
 */
export type CartGiftChoices = {
  claims: Record<string, GiftClaimLine[]>;
};

export const EMPTY_GIFT_CHOICES: CartGiftChoices = { claims: {} };

export function giftChoicesEmpty(choices: CartGiftChoices): boolean {
  return !Object.keys(choices.claims).length;
}

/** JSON tersimpan → pilihan hadiah. Bentuk lama (tanpa kunci `gifts`) / rusak = kosong. */
export function normalizeSavedGiftChoices(raw: unknown): CartGiftChoices {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return EMPTY_GIFT_CHOICES;
  const gifts = (raw as Record<string, unknown>).gifts;
  if (!gifts || typeof gifts !== "object" || Array.isArray(gifts)) return EMPTY_GIFT_CHOICES;
  const claims: Record<string, GiftClaimLine[]> = {};
  const rawClaims = (gifts as Record<string, unknown>).claims;
  if (rawClaims && typeof rawClaims === "object" && !Array.isArray(rawClaims)) {
    for (const [ruleId, lines] of Object.entries(rawClaims as Record<string, unknown>)) {
      if (!ruleId || !Array.isArray(lines)) continue;
      const clean = lines
        .map((l) => ({
          variantId: String((l as { variantId?: unknown })?.variantId ?? ""),
          qty: Math.floor(Number((l as { qty?: unknown })?.qty))
        }))
        .filter((l) => l.variantId && Number.isFinite(l.qty) && l.qty > 0 && l.qty <= 999);
      if (clean.length) claims[ruleId] = clean;
    }
  }
  return { claims };
}

/** Σ qty per varian di keranjang — hadiah Ready Stock berbagi stok dengan baris biasa. */
export function cartQtyByVariant(carts: readonly GiftCartProduct[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const c of carts) {
    for (const l of c.lines) {
      const qty = Number.isFinite(l.qty) ? Math.max(0, Math.floor(l.qty)) : 0;
      if (qty) out.set(l.variationId, (out.get(l.variationId) ?? 0) + qty);
    }
  }
  return out;
}

/** Nama hadiah dalam kalimat: produk + varian dikunci, atau produk + opsi batasnya. */
export function giftRewardName(rule: GiftRule, reward: GiftProductInfo, locked: GiftRewardChoice | null): string {
  return locked
    ? `${reward.name} ${locked.name}`.trim()
    : giftTargetLabel(reward.name, rule.rewardOptionIds, reward.optionNameOf);
}

/**
 * Nama barang syarat aturan PRODUK TUNGGAL (produk + opsi batasnya); aturan
 * bertarget memakai `giftTargetDescription` di dalam `giftRuleSentence`. null =
 * produk syarat tak terbaca (aturan tak ditawarkan).
 */
export function giftTriggerName(rule: GiftRule, products: ReadonlyMap<string, GiftProductInfo>): string | null {
  if (rule.triggerScope === "target") return "";
  const trigger = rule.triggerProductId ? products.get(rule.triggerProductId) : undefined;
  return trigger ? giftTargetLabel(trigger.name, rule.triggerOptionIds, trigger.optionNameOf) : null;
}

/**
 * Kartu hadiah keranjang. Setiap baris BIASA keranjang punya SATU pemilik —
 * aturan berjalan & berhak level tertua yang mencakupnya (H24) — dan hanya
 * dihitung untuk pemiliknya. Aturan tanpa baris milik (0 pcs / Rp 0) tak
 * dimunculkan. `claims` = pilihan pembeli per aturan (boleh basi — dirapikan
 * `normalizeGiftClaim`). Hadiah tak bisa dilewati (H40). Baris produk yang tak
 * terbaca tak dihitung (gagal-tutup — server akan menolaknya).
 *
 * `carry` (pembeli yang login, mig 8880) = sisa pesanan sebelumnya per aturan.
 * Ia dihitung hanya bila server memakainya sesudah checkout (gabungan lintas
 * pesanan): aturan kelipatan (sisa ikut pembelian yang haknya naik, H56), atau
 * aturan sekali yang BELUM tercapai oleh keranjang saja (sisa + keranjang jadi
 * pembelian baru). Klaim tetap sebatas hak keranjang; selebihnya
 * `autoEntitlement`.
 *
 * `open` (Hadiah Tahap 6-2) = pembelian LAMA per aturan yang masih bisa
 * bertambah (`my_gift_open_purchases`, tertua dulu). Mesin mencobanya lebih dulu
 * (langkah a'): yang pertama yang haknya naik menyerap barang yang dicakup
 * snapshot-nya → `oldPcs`. Checkout (`mode` bawaan): keranjang yang mencapai
 * syarat sendiri jadi pembelian keranjang LEBIH DULU (klaim), jadi hanya sisa
 * pesanan sebelumnya yang dicoba ke pembelian lama. Editor (`mode: "edit"`,
 * `giftNewPurchases`): tak ada klaim — barang yang tetap tak terikat ikut
 * dicoba. Tanpa `open` hasilnya persis seperti sebelum 6-2.
 */
export function cartGiftEntries(input: {
  data: StorefrontGiftData;
  carts: readonly GiftCartProduct[];
  /** Slot level pembeli (`bundleCustomerSlot`); null = pelanggan umum / tamu. */
  slot: number | null;
  now: Date;
  claims?: ReadonlyMap<string, readonly GiftClaimLine[]>;
  carry?: ReadonlyMap<string, GiftCarry>;
  open?: ReadonlyMap<string, readonly GiftOpenPurchase[]>;
  mode?: "checkout" | "edit";
}): GiftCartEntry[] {
  const { data, carts, slot, now } = input;
  const inCart = cartQtyByVariant(carts);

  // Pemilik tiap baris → Σ pcs, Σ pcs berharga, Σ nilai per aturan.
  const candidates = giftOwnerCandidates(data.rules, { slot, now });
  const owned = new Map<
    string,
    {
      pcs: number;
      pricedPcs: number;
      value: number;
      keys: GiftLineKey[];
      pricedKeys: GiftLineKey[];
      lines: GiftOwnedLine[];
    }
  >();
  for (const cart of carts) {
    const info = data.products.get(cart.productId);
    if (!info) continue;
    const ref: GiftProductRef = {
      id: cart.productId,
      brand: info.brand,
      category: info.category,
      productType: info.productType
    };
    // Produk tanpa grup variasi: barisnya = "tanpa varian" (cocok dengan aturan
    // tanpa batas varian — cermin `x.variant_id is null`).
    const noVariants = info.optionGroupOf.size === 0;
    for (const line of cart.lines) {
      const qty = Number.isFinite(line.qty) ? Math.max(0, Math.floor(line.qty)) : 0;
      if (!qty) continue;
      const variantId = noVariants ? null : line.variationId;
      const lineRef: GiftLineRef = {
        variantId,
        optionIds: variantId ? data.variantOptionIds.get(variantId) ?? null : null
      };
      const owner = giftLineOwner(candidates, ref, lineRef, info.optionGroupOf);
      if (!owner) continue;
      const acc = owned.get(owner.id) ?? { pcs: 0, pricedPcs: 0, value: 0, keys: [], pricedKeys: [], lines: [] };
      const value = giftLineValue(qty, line.unitPrice);
      const key = { productId: cart.productId, variationId: line.variationId };
      acc.pcs += qty;
      acc.keys.push(key);
      acc.lines.push({ product: ref, line: lineRef, optionGroupOf: info.optionGroupOf, qty, value });
      if (value > 0) {
        acc.pricedPcs += qty;
        acc.value += value;
        acc.pricedKeys.push(key);
      }
      owned.set(owner.id, acc);
    }
  }

  const out: GiftCartEntry[] = [];
  for (const rule of data.rules) {
    const acc = owned.get(rule.id);
    if (!acc || !rule.rewardProductId) continue;
    if (!isGiftRuleRunning(rule, now) || !giftRuleAllowsLevel(rule, slot)) continue;
    const reward = data.products.get(rule.rewardProductId);
    const triggerName = giftTriggerName(rule, data.products);
    if (!reward || triggerName === null) continue;

    // Ukuran nilai: hanya baris berharga yang dihitung (H25 — harga menyusul tidak).
    const measure = rule.triggerMeasure;
    const triggerPcs = measure === "nilai" ? acc.pricedPcs : acc.pcs;
    const triggerValue = Math.round(acc.value);
    if (triggerPcs <= 0) continue;

    const cartFull = giftEntitlementMeasure(rule, triggerPcs, triggerValue);
    const carried = input.carry?.get(rule.id);
    // H15: hak yang melebihi sisa kuota diberikan sebatas sisanya; dorongan
    // "tambah …" tak menjanjikan hadiah di atas sisa itu.
    const remaining = giftQuotaCap(data.quotas.get(rule.id));
    const cap = remaining ?? Number.POSITIVE_INFINITY;

    // Langkah (a') — pembelian LAMA aturan ini yang haknya naik (Tahap 6-2).
    // Checkout: keranjang yang mencapai syarat sendiri diklaim lebih dulu, jadi
    // barangnya tak ikut dicoba ke pembelian lama.
    const claimsCart = (input.mode ?? "checkout") === "checkout" && cartFull > 0;
    const old = giftOldAbsorb(input.open?.get(rule.id) ?? [], claimsCart ? [] : acc.lines);

    let entitlement: number;
    let totalEntitlement: number;
    let carryPcs = 0;
    let carryValue = 0;
    let oldPcs = 0;
    let quotaCapped: boolean;
    let missing = 0;
    let missingAmount = 0;
    let nextEntitlement: number;
    if (!old) {
      // Sisa pesanan sebelumnya (H55) — hanya bila server memakainya: aturan
      // kelipatan, atau aturan sekali yang belum tercapai oleh keranjang saja.
      const carriedPcs = carried ? Math.max(0, Math.floor(measure === "nilai" ? carried.pricedPcs : carried.pcs)) : 0;
      const useCarry = carriedPcs > 0 && (rule.repeat || cartFull <= 0);
      carryPcs = useCarry ? carriedPcs : 0;
      carryValue = useCarry && carried ? Math.max(0, Math.round(carried.value)) : 0;
      const totalPcs = triggerPcs + carryPcs;
      const totalValue = triggerValue + carryValue;
      const fullEntitlement = giftEntitlementMeasure(rule, totalPcs, totalValue);
      totalEntitlement = Math.min(fullEntitlement, cap);
      entitlement = Math.min(cartFull, cap);
      quotaCapped = remaining !== null && fullEntitlement > remaining;
      const capped = remaining !== null && fullEntitlement >= remaining;
      missing = measure === "pcs" && !capped ? giftMissingPcs(totalPcs, rule) : 0;
      missingAmount = measure === "nilai" && !capped ? giftMissingAmount(totalValue, rule) : 0;
      nextEntitlement =
        missing > 0
          ? Math.min(giftEntitlementMeasure(rule, totalPcs + missing, totalValue), cap)
          : missingAmount > 0
            ? Math.min(giftEntitlementMeasure(rule, totalPcs, totalValue + missingAmount), cap)
            : totalEntitlement;
    } else if (claimsCart) {
      // Pembelian keranjang = isi keranjang saja (mesin keluar dari langkah a'
      // begitu pembelian lama menyerap sisa pesanan sebelumnya).
      entitlement = Math.min(cartFull, cap);
      totalEntitlement = entitlement;
      oldPcs = Math.max(0, Math.min(old.pcs, cap - entitlement));
      quotaCapped = remaining !== null && cartFull + old.pcs > remaining;
      const capped = remaining !== null && cartFull + old.pcs >= remaining;
      missing = measure === "pcs" && !capped ? giftMissingPcs(triggerPcs, rule) : 0;
      missingAmount = measure === "nilai" && !capped ? giftMissingAmount(triggerValue, rule) : 0;
      nextEntitlement =
        missing > 0
          ? Math.min(giftEntitlementMeasure(rule, triggerPcs + missing, triggerValue), cap)
          : missingAmount > 0
            ? Math.min(giftEntitlementMeasure(rule, triggerPcs, triggerValue + missingAmount), cap)
            : totalEntitlement;
    } else {
      // Barang baru ikut pembelian lama; yang tak dicakup snapshot-nya jadi
      // pembelian baru bila mencapai syarat (langkah b'). Hak naik tak rata
      // (pembelian lama vs baru), jadi tanpa dorongan "tambah …".
      const restPcs = triggerPcs - (measure === "nilai" ? old.cartPricedPcs : old.cartPcs);
      const restValue = Math.max(0, triggerValue - Math.round(old.cartValue));
      const restCarry = {
        pcs: Math.max(0, (carried?.pcs ?? 0) - old.carry.pcs),
        pricedPcs: Math.max(0, (carried?.pricedPcs ?? 0) - old.carry.pricedPcs),
        value: Math.max(0, Math.round((carried?.value ?? 0) - old.carry.value))
      };
      const restCarryPcs = measure === "nilai" ? restCarry.pricedPcs : restCarry.pcs;
      const restFull =
        restPcs > 0 ? giftEntitlementMeasure(rule, restPcs + restCarryPcs, restValue + restCarry.value) : 0;
      entitlement = 0;
      oldPcs = Math.min(old.pcs, cap);
      totalEntitlement = Math.max(0, Math.min(restFull, cap - oldPcs));
      if (totalEntitlement > 0) {
        carryPcs = restCarryPcs;
        carryValue = restCarry.value;
      }
      quotaCapped = remaining !== null && old.pcs + restFull > remaining;
      nextEntitlement = totalEntitlement;
    }
    const autoEntitlement = Math.max(0, totalEntitlement - entitlement);
    // Stok Ready Stock yang sudah dipakai baris biasa di keranjang ini tak bisa
    // jadi hadiah lagi (server menjumlah keduanya — "beli 2 gratis 1" butuh 3).
    const variants = reward.variants.map((v) =>
      v.stock === null ? v : { ...v, stock: Math.max(0, v.stock - (inCart.get(v.id) ?? 0)) }
    );
    const choices = giftRewardChoices(rule, variants, reward.optionGroupOf, 1);
    const locked = lockedGiftReward(choices);
    const sentence = giftRuleSentence(rule, { trigger: triggerName, reward: giftRewardName(rule, reward, locked) });

    let status: GiftCartStatus;
    let claim: GiftClaimLine[] = [];
    if (remaining === 0) status = "kuota_habis";
    else if (totalEntitlement <= 0 && oldPcs <= 0) status = "kurang";
    else if (!isOrderableStatus(reward.status, reward.productType)) status = "tidak_bisa_dipesan";
    else if (entitlement <= 0) status = choices.some((c) => c.selectable) ? "otomatis" : "habis";
    else {
      const saved = input.claims?.get(rule.id);
      claim = saved ? normalizeGiftClaim(saved, entitlement, choices) : defaultGiftClaim(entitlement, choices);
      status = claim.length ? "siap" : "habis";
    }
    out.push({
      rule,
      sentence,
      reward,
      measure,
      triggerPcs,
      triggerValue,
      unpricedPcs: measure === "nilai" ? acc.pcs - acc.pricedPcs : 0,
      syaratKeys: measure === "nilai" ? acc.pricedKeys : acc.keys,
      entitlement,
      carryPcs,
      carryValue,
      autoEntitlement: status === "siap" || status === "otomatis" ? autoEntitlement : 0,
      oldPcs: status === "siap" || status === "otomatis" ? oldPcs : 0,
      remaining,
      quotaCapped,
      missing,
      missingAmount,
      nextEntitlement,
      choices,
      locked,
      claim,
      status
    });
  }
  return out;
}

/**
 * Pembelian berhadiah BARU yang akan dibuat server sesudah simpan (H59), atau
 * — `old: true` (Hadiah Tahap 6-2) — hadiah yang bertambah di pembelian LAMA
 * aturan itu karena barangnya ikut ke sana (langkah a').
 */
export type GiftNewPurchase = { ruleId: string; name: string; pcs: number; old?: boolean };

/**
 * Pembelian lama per aturan (urutan server — tertua dulu) tanpa yang barang
 * syaratnya ada di pesanan `excludeOrderIds`: pesanan yang sedang disunting
 * dimodelkan langkah (a) editor, bukan (a').
 */
export function giftOpenByRule(
  open: readonly GiftOpenPurchase[],
  excludeOrderIds: readonly string[] = []
): Map<string, GiftOpenPurchase[]> {
  const skip = new Set(excludeOrderIds);
  const out = new Map<string, GiftOpenPurchase[]>();
  for (const p of open) {
    if (p.orderIds.some((o) => skip.has(o))) continue;
    out.set(p.ruleId, [...(out.get(p.ruleId) ?? []), p]);
  }
  return out;
}

/**
 * Pratinjau pembelian berhadiah BARU di modal edit pesanan (H59, mig 8880):
 * baris pesanan yang TETAP tak terikat sesudah simpan (`giftEditAllocation`
 * `leftover`, atau seluruh baris pesanan tanpa pembelian) + sisa pesanan lain
 * pelanggan itu (`carry`) → per aturan pemilik (H24), pcs hadiah yang diberikan
 * mesin `gift_auto_attach_order` (langkah a'/b'). Rumus keranjang dipakai apa
 * adanya: pcs = hak + hak otomatis; hadiah yang tak bisa diberikan (stok, kuota,
 * produk hadiah tak bisa dipesan) tak dijanjikan.
 */
export function giftNewPurchases(input: {
  data: StorefrontGiftData;
  productId: string;
  lines: ReadonlyArray<{ variationId: string; qty: number; unitPrice: number | null }>;
  slot: number | null;
  now: Date;
  carry?: ReadonlyMap<string, GiftCarry>;
  /** Pembelian lama yang masih bisa bertambah (`giftOpenByRule`, Tahap 6-2). */
  open?: ReadonlyMap<string, readonly GiftOpenPurchase[]>;
}): GiftNewPurchase[] {
  const lines = input.lines.filter((l) => Number.isFinite(l.qty) && l.qty > 0);
  if (!lines.length) return [];
  const out: GiftNewPurchase[] = [];
  for (const e of cartGiftEntries({
    data: input.data,
    carts: [{ productId: input.productId, lines }],
    slot: input.slot,
    now: input.now,
    carry: input.carry,
    open: input.open,
    mode: "edit"
  })) {
    if (e.status !== "siap" && e.status !== "otomatis") continue;
    if (e.oldPcs > 0) out.push({ ruleId: e.rule.id, name: e.rule.name, pcs: e.oldPcs, old: true });
    const pcs = e.entitlement + e.autoEntitlement;
    if (pcs > 0) out.push({ ruleId: e.rule.id, name: e.rule.name, pcs });
  }
  return out;
}

/**
 * Muatan `p_gifts` dari kartu yang siap. Hadiah selalu ikut bila berhak (H40):
 * pcs yang belum dipilih diisi varian bawaan (`fillGiftClaim`).
 */
export function cartGiftPayload(entries: readonly GiftCartEntry[]) {
  return giftCheckoutPayload(
    entries
      .filter((e) => e.status === "siap")
      .map((e) => ({ ruleId: e.rule.id, lines: fillGiftClaim(e.claim, e.entitlement, e.choices) }))
  );
}

/**
 * Pilihan pembeli sesudah satu ketukan pada varian `variantId` dengan arah
 * `delta` (+1 / −1). Hak 1 pcs = memilih (ganti varian); hak > 1 = membagi pcs
 * antar varian, Σ tak pernah melebihi hak & stok varian. Varian yang tak bisa
 * dipilih (H1 Harga Tambahan, stok habis) tak mengubah apa pun.
 */
export function stepGiftClaim(
  entry: Pick<GiftCartEntry, "entitlement" | "choices" | "claim">,
  variantId: string,
  delta: 1 | -1
): GiftClaimLine[] {
  const choice = entry.choices.find((c) => c.id === variantId);
  if (!choice || !choice.selectable) return entry.claim.map((l) => ({ ...l }));
  if (entry.entitlement === 1) {
    return delta > 0 ? [{ variantId, qty: 1 }] : entry.claim.map((l) => ({ ...l }));
  }
  const next = entry.claim.map((l) => ({ ...l }));
  const total = next.reduce((s, l) => s + l.qty, 0);
  const line = next.find((l) => l.variantId === variantId);
  if (delta > 0) {
    const cap = choice.stock === null ? Infinity : choice.stock;
    if ((line?.qty ?? 0) >= cap) return next;
    if (total >= entry.entitlement) {
      // Hak sudah penuh: pindahkan 1 pcs dari varian lain yang terakhir dipilih.
      const donor = [...next].reverse().find((l) => l.variantId !== variantId && l.qty > 0);
      if (!donor) return next;
      donor.qty -= 1;
    }
    if (line) line.qty += 1;
    else next.push({ variantId, qty: 1 });
  } else if (line && line.qty > 0) {
    line.qty -= 1;
  }
  return next.filter((l) => l.qty > 0);
}

/** Produk yang di checkout ini HANYA berisi hadiah — tak menembak Purchase / atribusi. */
export function giftOnlyProductIds(
  entries: readonly GiftCartEntry[],
  orderedProductIds: ReadonlySet<string>
): Set<string> {
  const out = new Set<string>();
  for (const e of entries) {
    if (e.status === "siap" && !orderedProductIds.has(e.reward.id)) out.add(e.reward.id);
  }
  return out;
}

// ─── Halaman produk & etalase ────────────────────────────────────────────────

export type ProductGiftOffer = {
  rule: GiftRule;
  sentence: string;
  reward: GiftProductInfo;
  /** Sisa kuota untuk pembeli ini; null = tak terbatas. */
  remaining: number | null;
};

/**
 * Aturan yang hadiahnya memang bisa diberikan sekarang: produk hadiah bisa
 * dipesan, ada varian tanpa Harga Tambahan yang berstok, kuotanya belum habis,
 * dan (produk tunggal) produk syaratnya terbaca. null = tidak.
 */
export function giftRuleOffer(data: StorefrontGiftData, rule: GiftRule): ProductGiftOffer | null {
  if (!rule.rewardProductId) return null;
  const reward = data.products.get(rule.rewardProductId);
  const triggerName = giftTriggerName(rule, data.products);
  if (!reward || triggerName === null || !isOrderableStatus(reward.status, reward.productType)) return null;
  const remaining = giftQuotaCap(data.quotas.get(rule.id));
  if (remaining === 0) return null;
  const choices = giftRewardChoices(rule, reward.variants, reward.optionGroupOf, 1);
  if (!choices.some((c) => c.selectable)) return null;
  const locked = lockedGiftReward(choices);
  return {
    rule,
    reward,
    remaining,
    sentence: giftRuleSentence(rule, { trigger: triggerName, reward: giftRewardName(rule, reward, locked) })
  };
}

/**
 * Blok "Gratis hadiah" halaman produk: aturan berjalan yang berhak bagi pembeli
 * ini, mencakup produk ini, dan hadiahnya bisa diberikan sekarang. Pemilik
 * (H24) dinilai tingkat PRODUK: begitu aturan yang lebih tua mencakup SELURUH
 * varian produk ini (bertarget, atau produk tunggal tanpa batas varian), aturan
 * yang lebih muda tak memiliki satu baris pun — tak ditawarkan. Keranjang
 * menilai per baris (`cartGiftEntries`). `candidates` boleh dihitung sekali
 * oleh pemanggil (`giftOwnerCandidates`).
 */
export function productGiftOffers(
  data: StorefrontGiftData,
  product: GiftProductRef,
  slot: number | null,
  now: Date,
  candidates: readonly GiftRule[] = giftOwnerCandidates(data.rules, { slot, now })
): ProductGiftOffer[] {
  const owners = new Set<string>();
  for (const rule of candidates) {
    if (!giftRuleCoversProduct(rule, product)) continue;
    owners.add(rule.id);
    if (rule.triggerScope === "target" || rule.triggerOptionIds.length === 0) break;
  }
  if (!owners.size) return [];
  const out: ProductGiftOffer[] = [];
  for (const rule of data.rules) {
    if (!owners.has(rule.id)) continue;
    const offer = giftRuleOffer(data, rule);
    if (offer) out.push(offer);
  }
  return out;
}
